import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faTerminal, faCodeBranch } from '@fortawesome/free-solid-svg-icons';
import { faJava, faPython, faJs, faDocker, faGithub, faRust } from '@fortawesome/free-brands-svg-icons';

const Projects = () => {
  const projects = [
    {
      title: "Posterly.ai",
      description: "AI-powered poster generator and full-stack SaaS that enables users to create stunning visuals from text prompts. Features a seamless checkout flow and production-ready architecture.",
      techStack: ["Next.js", "TypeScript", "Tailwind CSS", "OpenAI API", "PostgreSQL", "Prisma"],
      githubUrl: "https://github.com/Anayo-Anyafulu", // Placeholder if private
      liveUrl: "https://posterlyx.vercel.app",
      image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&h=300&fit=crop"
    },
    {
      title: "Spawn",
      description: "A BitTorrent-inspired P2P file distribution system built from scratch. Implements custom networking protocols for efficient chunk-based file sharing across a distributed network.",
      techStack: ["Rust", "Tokio", "Networking", "P2P", "Async Programming"],
      githubUrl: "https://github.com/Anayo-Anyafulu/spawn",
      liveUrl: null,
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc51?w=500&h=300&fit=crop"
    },
    {
      title: "Apache OpenDAL Contribution",
      description: "Contributing to the Apache OpenDAL project as a GSoC 2026 applicant. Focused on improving the data access layer for various storage services and enhancing the Rust implementation.",
      techStack: ["Rust", "Open Source", "Apache Software Foundation", "GSoC 2026"],
      githubUrl: "https://github.com/apache/opendal",
      liveUrl: "https://opendal.apache.org/",
      image: "https://images.unsplash.com/photo-1510915228340-29c85a43dcfe?w=500&h=300&fit=crop"
    },
    {
      title: "Broadcast Server",
      description: "A command-line broadcast server that allows multiple clients to connect and exchange messages in real time. Built with Python, this CLI application demonstrates socket programming and real-time communication concepts.",
      techStack: ["Python", "Socket Programming", "CLI", "Real-time Communication"],
      githubUrl: "https://github.com/Anayo-Anyafulu/broadcast",
      liveUrl: null,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=300&fit=crop"
    },
    {
      title: "MovieList - Spring Boot REST API",
      description: "A Movie List Management System built with Spring Boot, allowing you to manage movies, track watched status, and perform CRUD operations via REST API. Features H2 database integration and comprehensive movie management capabilities.",
      techStack: ["Java", "Spring Boot", "Spring Data JPA", "H2 Database", "REST API", "Maven"],
      githubUrl: "https://github.com/Anayo-Anyafulu/MovieList",
      liveUrl: null,
      image: "https://images.unsplash.com/photo-1489599809994-0a0a0a0a0a0a?w=500&h=300&fit=crop"
    }
  ];

  const getTechIcon = (tech) => {
    switch (tech.toLowerCase()) {
      case 'rust': return faRust;
      case 'java': return faJava;
      case 'python': return faPython;
      case 'typescript':
      case 'javascript':
      case 'next.js': return faJs;
      case 'docker': return faDocker;
      case 'open source': return faCodeBranch;
      case 'p2p':
      case 'networking':
      case 'tokio': return faTerminal;
      default: return faJs;
    }
  };

  return (
    <section id="projects" className="py-20 bg-dark-900">
      <div className="container-max-width section-padding">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Featured Projects</span>
          </h2>
          <p className="text-xl text-dark-300 max-w-3xl mx-auto">
            Backend systems and APIs I've built to solve real-world problems
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <div 
              key={index}
              className="bg-dark-800 rounded-xl overflow-hidden hover:bg-dark-700 transition-all duration-300 card-hover animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Project Image */}
              <div className="h-48 bg-gradient-to-r from-primary-600 to-primary-800 flex items-center justify-center">
                <div className="text-center text-white">
                  <FontAwesomeIcon icon={getTechIcon(project.techStack[0])} className="text-6xl mb-4 opacity-80" />
                  <p className="text-lg font-medium">{project.techStack[0]} Project</p>
                </div>
              </div>

              <div className="p-6">
                {/* Project Title */}
                <h3 className="text-2xl font-bold text-white mb-3">
                  {project.title}
                </h3>

                {/* Project Description */}
                <p className="text-dark-300 mb-6 leading-relaxed">
                  {project.description}
                </p>

                {/* Tech Stack */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-primary-400 mb-3 uppercase tracking-wide">
                    Tech Stack
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {project.techStack.map((tech, techIndex) => (
                      <span 
                        key={techIndex}
                        className="px-3 py-1 bg-dark-700 text-dark-300 rounded-full text-sm font-medium hover:bg-primary-600 hover:text-white transition-all duration-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Project Links */}
                <div className="flex space-x-4">
                  <a 
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 bg-dark-700 hover:bg-dark-600 text-dark-300 hover:text-white px-4 py-2 rounded-lg transition-all duration-300"
                  >
                    <FontAwesomeIcon icon={faGithub} />
                    <span>Code</span>
                  </a>
                  
                  {project.liveUrl && (
                    <a 
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-all duration-300"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                      <span>Live Demo</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View More Projects */}
        <div className="text-center mt-12">
          <a 
            href="https://github.com/Anayo-Anyafulu"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105"
          >
            <FontAwesomeIcon icon={faGithub} />
            <span>View All Projects on GitHub</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Projects;
